import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [role, setRole] = useState("manager");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const roleParam = new URLSearchParams(loc.search).get("role");
  const isSuper = roleParam === "super";
  const isAdviser = roleParam === "adviser";

  React.useEffect(() => {
    if (localStorage.getItem("omi_remember") === "1") {
      setRemember(true);
      setEmail(localStorage.getItem("omi_saved_email") || "");
      setPassword(localStorage.getItem("omi_saved_pw") || "");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (remember) {
        localStorage.setItem("omi_remember", "1");
        localStorage.setItem("omi_saved_email", email);
        localStorage.setItem("omi_saved_pw", password);
      } else {
        localStorage.removeItem("omi_remember");
        localStorage.removeItem("omi_saved_email");
        localStorage.removeItem("omi_saved_pw");
      }
      const loginAs = isSuper ? "admin" : isAdviser ? "user" : role;
      const u = await login(email, password, loginAs);
      toast.success("Welcome back!");
      const dest = loc.state?.from || (u.role === "user" ? "/dashboard" : "/admin");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your Credit Kendra dashboard & wallet."
      footer={<>New here? <Link to="/register" className="font-semibold text-emerald-brand">Create an account</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {isSuper ? (
          <div className="bg-[#0B223D] text-white text-xs font-semibold rounded-lg px-3 py-2" data-testid="login-super-badge">Super Admin login</div>
        ) : isAdviser ? (
          <div className="bg-teal-600 text-white text-xs font-semibold rounded-lg px-3 py-2" data-testid="login-adviser-badge">Adviser login</div>
        ) : (
          <div>
            <Label className="font-semibold text-slate-600">Login As</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="login-as-select" className="mt-1.5 w-full h-11 rounded-md border border-slate-200 px-3 text-sm bg-white">
              <option value="manager">Campaign Manager</option>
              <option value="staff">Employee</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">Apne registered email &amp; password se login karein. Access aapke assigned role par depend karta hai.</p>
            <div className="flex items-center gap-3 mt-3 text-xs">
              <Link to="/login?role=super" data-testid="login-switch-super" className="font-semibold text-[#0B223D] underline">Super Admin login</Link>
              <span className="text-slate-300">·</span>
              <Link to="/login?role=adviser" data-testid="login-switch-adviser" className="font-semibold text-emerald-brand underline">Adviser login</Link>
            </div>
          </div>
        )}
        <div>
          <Label className="font-semibold text-slate-600">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 h-11" placeholder="you@example.com" />
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Password</Label>
          <div className="relative mt-1.5">
            <Input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="h-11 pr-11" placeholder="••••••••" />
            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B223D] transition-colors">
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-500" data-testid="login-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Save login ID &amp; password
          </label>
          <Link to="/forgot-password" className="text-sm font-semibold text-slate-500 hover:text-emerald-brand">Forgot password?</Link>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
